import { T as utils_exports, _ as fromCodePoint, f as getKnownGlobalMarkdownState, g as countLines, h as runWithKnownGlobalMarkdownState, i as getParseDiagnostics, l as detectGlobalMarkdownState, m as resetKnownGlobalMarkdownState, n as beginParseDiagnostics, s as setStrategyDiagnostics, x as isValidEntityCode } from "./strategy_diagnostics-Bwt8UKDw.js";
import { a as normalizeLink, l as helpers_exports, n as parseInline, o as normalizeLinkText, r as ParserCore, s as validateLink, t as parse, u as Token } from "./parse-CZh7h656.js";
import { a as parseAsyncIterable, c as parseIterableToSink, d as chunkedParse, i as getAutoUnboundedDecision, l as parseStringUnbounded, n as recommendStreamChunkStrategy, o as parseAsyncIterableToSink, s as parseIterable, t as recommendFullChunkStrategy, u as shouldAutoUseUnbounded } from "./chunk_recommend-BIJ1gfCM.js";
import { decodeHTML } from "entities";
import LinkifyIt from "linkify-it";

//#region src/presets/commonmark.ts
const commonmark = {
	options: {
		html: true,
		xhtmlOut: true,
		breaks: false,
		langPrefix: "language-",
		linkify: false,
		typographer: false,
		quotes: "“”‘’",
		highlight: null,
		maxNesting: 20
	},
	components: {
		core: { rules: [
			"normalize",
			"block",
			"inline",
			"text_join"
		] },
		block: { rules: [
			"blockquote",
			"code",
			"fence",
			"heading",
			"hr",
			"html_block",
			"lheading",
			"list",
			"reference",
			"paragraph"
		] },
		inline: { rules: [
			"autolink",
			"backticks",
			"emphasis",
			"entity",
			"escape",
			"html_inline",
			"image",
			"link",
			"newline",
			"text"
		] },
		inline2: { rules: [
			"balance_pairs",
			"emphasis",
			"fragments_join"
		] }
	}
};
var commonmark_default = commonmark;

//#endregion
//#region src/presets/default.ts
const defaultPreset = {
	options: {
		html: false,
		xhtmlOut: false,
		breaks: false,
		langPrefix: "language-",
		linkify: false,
		typographer: false,
		quotes: "“”‘’",
		highlight: null,
		maxNesting: 100
	},
	components: {
		core: {},
		block: {},
		inline: {}
	}
};
var default_default = defaultPreset;

//#endregion
//#region src/presets/zero.ts
const zeroPreset = {
	options: {
		html: false,
		xhtmlOut: false,
		breaks: false,
		langPrefix: "language-",
		linkify: false,
		typographer: false,
		quotes: "“”‘’",
		maxNesting: 20
	},
	components: {
		core: { rules: [
			"normalize",
			"block",
			"inline",
			"text_join"
		] },
		block: { rules: ["paragraph"] },
		inline: { rules: ["text"] },
		inline2: { rules: ["balance_pairs", "fragments_join"] }
	}
};
var zero_default = zeroPreset;

//#endregion
//#region src/render/utils.ts
const HTML_ESCAPE_TEST_RE = /[&<>"]/;
const HTML_ESCAPE_REPLACE_RE = /[&<>"]/g;
const HTML_ESCAPE_AMP_RE = /&/g;
const HTML_ESCAPE_NO_AMP_RE = /[<>"]/g;
const HTML_REPLACEMENTS = {
	"&": "&amp;",
	"<": "&lt;",
	">": "&gt;",
	"\"": "&quot;"
};
function replaceUnsafeChar(ch) {
	return HTML_REPLACEMENTS[ch] || ch;
}
/**
* Escape HTML characters to prevent XSS
*/
function escapeHtml(str) {
	if (str.length === 0) return "";
	if (str.length < 32) {
		if (HTML_ESCAPE_TEST_RE.test(str)) return str.replace(HTML_ESCAPE_REPLACE_RE, replaceUnsafeChar);
		return str;
	}
	const hasAmp = str.includes("&");
	const hasLt = str.includes("<");
	const hasGt = str.includes(">");
	const hasQuot = str.includes("\"");
	if (!hasAmp && !hasLt && !hasGt && !hasQuot) return str;
	if (hasAmp && !hasLt && !hasGt && !hasQuot) return str.replace(HTML_ESCAPE_AMP_RE, "&amp;");
	if (!hasAmp) return str.replace(HTML_ESCAPE_NO_AMP_RE, replaceUnsafeChar);
	return str.replace(HTML_ESCAPE_REPLACE_RE, replaceUnsafeChar);
}
const UNESCAPE_ALL_RE = new RegExp(`${/\\([!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~])/g.source}|${/&([a-z#][a-z0-9]{1,31});/gi.source}`, "gi");
const DIGITAL_ENTITY_TEST_RE = /^#(?:x[a-f0-9]{1,8}|\d{1,8})$/i;
/**
* Unescape all backslash escapes and HTML entities
*/
function unescapeAll(str) {
	if (!str.includes("\\") && !str.includes("&")) return str;
	return str.replace(UNESCAPE_ALL_RE, (match, escaped, entity) => {
		if (escaped) return escaped;
		if (DIGITAL_ENTITY_TEST_RE.test(entity)) {
			const code = entity[1].toLowerCase() === "x" ? Number.parseInt(entity.slice(2), 16) : Number.parseInt(entity.slice(1), 10);
			return isValidEntityCode(code) ? fromCodePoint(code) : "�";
		}
		const decoded = decodeHTML(match);
		return decoded !== match ? decoded : match;
	});
}

//#endregion
//#region src/render/renderer.ts
function isPromiseLike(value) {
	return !!value && (typeof value === "object" || typeof value === "function") && typeof value.then === "function";
}
function ensureSyncResult(value, ruleName) {
	if (isPromiseLike(value)) throw new TypeError(`Renderer rule "${ruleName}" returned a Promise. Use renderAsync() instead.`);
	return value;
}
const resolveResult = (value) => isPromiseLike(value) ? value : Promise.resolve(value);
function renderAttrName(name) {
	switch (name) {
		case "alt":
		case "class":
		case "href":
		case "id":
		case "lang":
		case "rel":
		case "src":
		case "start":
		case "style":
		case "target":
		case "title": return name;
		default: return escapeHtml(name);
	}
}
function renderAttrsFromList(attrs) {
	if (!attrs || attrs.length === 0) return "";
	const firstAttr = attrs[0];
	let result = ` ${renderAttrName(firstAttr[0])}="${escapeHtml(firstAttr[1])}"`;
	for (let i = 1; i < attrs.length; i++) {
		const attr = attrs[i];
		result += ` ${renderAttrName(attr[0])}="${escapeHtml(attr[1])}"`;
	}
	return result;
}
function parseFenceInfo(info) {
	if (!info) return {
		langName: "",
		langAttrs: ""
	};
	let markerEnd = 0;
	while (markerEnd < info.length) {
		const ch = info.charCodeAt(markerEnd);
		if (ch === 32 || ch === 9 || ch === 10) break;
		markerEnd++;
	}
	if (markerEnd >= info.length) return {
		langName: info,
		langAttrs: ""
	};
	let attrsStart = markerEnd;
	while (attrsStart < info.length) {
		const ch = info.charCodeAt(attrsStart);
		if (ch !== 32 && ch !== 9 && ch !== 10) break;
		attrsStart++;
	}
	return {
		langName: info.slice(0, markerEnd),
		langAttrs: attrsStart < info.length ? info.slice(attrsStart) : ""
	};
}
function renderFence(token, highlighted, info, langName, options) {
	if (highlighted.indexOf("<pre") === 0) return `${highlighted}\n`;
	if (info) {
		if (!token.attrs || token.attrs.length === 0) return `<pre><code class="${escapeHtml(`${options.langPrefix ?? "language-"}${langName}`)}">${highlighted}</code></pre>\n`;
		const classIndex = token.attrIndex("class");
		const tmpAttrs = token.attrs ? token.attrs.slice() : [];
		const langClass = `${options.langPrefix ?? "language-"}${langName}`;
		if (classIndex < 0) tmpAttrs.push(["class", langClass]);
		else {
			tmpAttrs[classIndex] = tmpAttrs[classIndex].slice();
			tmpAttrs[classIndex][1] += ` ${langClass}`;
		}
		return `<pre><code${renderAttrsFromList(tmpAttrs)}>${highlighted}</code></pre>\n`;
	}
	return `<pre><code${renderAttrsFromList(token.attrs)}>${highlighted}</code></pre>\n`;
}
function renderCodeInlineToken(token) {
	if (!token.attrs || token.attrs.length === 0) return `<code>${escapeHtml(token.content)}</code>`;
	return `<code${renderAttrsFromList(token.attrs)}>${escapeHtml(token.content)}</code>`;
}
function renderCodeBlockToken(token) {
	const content = escapeHtml(token.content);
	if (!token.attrs) return `<pre><code>${content}</code></pre>\n`;
	return `<pre${renderAttrsFromList(token.attrs)}><code>${content}</code></pre>\n`;
}
function renderFastBlockOpenWithInline(token, prefix) {
	const attrs = token.attrs;
	if (!attrs || attrs.length === 0) switch (token.type) {
		case "paragraph_open": return `${prefix}<p>`;
		case "heading_open": return `<${token.tag}>`;
		case "td_open": return `${prefix}<td>`;
		case "th_open": return `${prefix}<th>`;
		default: return null;
	}
	if (attrs.length === 1 && attrs[0][0] === "style") {
		if (token.type === "td_open") return `${prefix}<td style="${escapeHtml(attrs[0][1])}">`;
		if (token.type === "th_open") return `${prefix}<th style="${escapeHtml(attrs[0][1])}">`;
	}
	return null;
}
function renderLinkOpenToken(token) {
	const attrs = token.attrs;
	if (!attrs || attrs.length === 0) return "<a>";
	if (attrs.length === 1) return `<a ${renderAttrName(attrs[0][0])}="${escapeHtml(attrs[0][1])}">`;
	if (attrs.length === 2) return `<a ${renderAttrName(attrs[0][0])}="${escapeHtml(attrs[0][1])}" ${renderAttrName(attrs[1][0])}="${escapeHtml(attrs[1][1])}">`;
	return `<a${renderAttrsFromList(attrs)}>`;
}
function canUseLinkInlineFastPath(token) {
	switch (token.type) {
		case "text":
		case "text_special":
		case "softbreak":
		case "hardbreak":
		case "html_inline":
		case "code_inline":
		case "image": return true;
		default: return false;
	}
}
function canUseWrappedInlineFastPath(token) {
	switch (token.type) {
		case "text":
		case "text_special":
		case "softbreak":
		case "hardbreak":
		case "html_inline":
		case "code_inline": return true;
		default: return false;
	}
}
function renderFlatToken(token, xhtmlOut) {
	if (token.hidden) return "";
	const attrs = token.attrs;
	const nesting = token.nesting;
	const tag = token.tag;
	if (!attrs || attrs.length === 0) {
		if (nesting === 0) return xhtmlOut ? `<${tag} />` : `<${tag}>`;
		return nesting === -1 ? `</${tag}>` : `<${tag}>`;
	}
	let result = (nesting === -1 ? "</" : "<") + tag + renderAttrsFromList(attrs);
	if (nesting === 0 && xhtmlOut) result += " /";
	return `${result}>`;
}
const DEFAULT_RENDERER_OPTIONS = {
	langPrefix: "language-",
	xhtmlOut: false,
	breaks: false
};
const hasOwn = Object.prototype.hasOwnProperty;
const defaultRules = {
	code_inline(tokens, idx) {
		return renderCodeInlineToken(tokens[idx]);
	},
	code_block(tokens, idx) {
		return renderCodeBlockToken(tokens[idx]);
	},
	fence(tokens, idx, options, _env, _self) {
		const token = tokens[idx];
		const info = token.info ? unescapeAll(token.info).trim() : "";
		const { langName, langAttrs } = parseFenceInfo(info);
		const highlight = options.highlight;
		const fallback = escapeHtml(token.content);
		if (!highlight) return renderFence(token, fallback, info, langName, options);
		const highlighted = highlight(token.content, langName, langAttrs);
		if (isPromiseLike(highlighted)) return highlighted.then((res) => renderFence(token, res || fallback, info, langName, options));
		return renderFence(token, highlighted || fallback, info, langName, options);
	},
	image(tokens, idx, options, env, self) {
		const token = tokens[idx];
		const altText = self.renderInlineAsText(token.children || [], options, env);
		const altIndex = token.attrIndex("alt");
		if (altIndex >= 0 && token.attrs) token.attrs[altIndex][1] = altText;
		else if (token.attrs) token.attrs.push(["alt", altText]);
		else token.attrs = [["alt", altText]];
		return renderFlatToken(token, options.xhtmlOut === true);
	},
	hardbreak(_tokens, _idx, options) {
		return options.xhtmlOut ? "<br />\n" : "<br>\n";
	},
	softbreak(_tokens, _idx, options) {
		return options.breaks ? options.xhtmlOut ? "<br />\n" : "<br>\n" : "\n";
	},
	text(tokens, idx) {
		return escapeHtml(tokens[idx].content);
	},
	text_special(tokens, idx) {
		return escapeHtml(tokens[idx].content);
	},
	html_block(tokens, idx) {
		return tokens[idx].content;
	},
	html_inline(tokens, idx) {
		return tokens[idx].content;
	}
};
function renderFenceSyncToken(token, options, _self) {
	const info = token.info ? unescapeAll(token.info).trim() : "";
	const { langName, langAttrs } = parseFenceInfo(info);
	const highlight = options.highlight;
	const fallback = escapeHtml(token.content);
	if (!highlight) return renderFence(token, fallback, info, langName, options);
	const highlighted = highlight(token.content, langName, langAttrs);
	if (isPromiseLike(highlighted)) throw new TypeError("Renderer rule \"fence\" returned a Promise. Use renderAsync() instead.");
	return renderFence(token, highlighted || fallback, info, langName, options);
}
function renderSingleInlineTokenSync(tokens, options, env, self, rules, inlineBreak, softbreak) {
	const token = tokens[0];
	switch (token.type) {
		case "text":
			if (rules.text === defaultRules.text) return token.content.length === 0 ? "" : escapeHtml(token.content);
			break;
		case "text_special":
			if (rules.text_special === defaultRules.text_special) return token.content.length === 0 ? "" : escapeHtml(token.content);
			break;
		case "softbreak":
			if (rules.softbreak === defaultRules.softbreak) return softbreak;
			break;
		case "hardbreak":
			if (rules.hardbreak === defaultRules.hardbreak) return inlineBreak;
			break;
		case "html_inline":
			if (rules.html_inline === defaultRules.html_inline) return token.content;
			break;
		case "code_inline":
			if (rules.code_inline === defaultRules.code_inline) return renderCodeInlineToken(token);
			break;
		default: break;
	}
	const rule = rules[token.type];
	if (!rule) return renderFlatToken(token, options.xhtmlOut === true);
	const rendered = rule(tokens, 0, options, env, self);
	if (typeof rendered === "string") return rendered;
	return ensureSyncResult(rendered, token.type);
}
var Renderer = class {
	rules;
	baseOptions;
	normalizedBase;
	constructor(options = {}) {
		this.baseOptions = { ...options };
		this.normalizedBase = this.buildNormalizedBase();
		this.rules = { ...defaultRules };
	}
	set(options) {
		this.baseOptions = {
			...this.baseOptions,
			...options
		};
		this.normalizedBase = this.buildNormalizedBase();
		return this;
	}
	render(tokens, options, env) {
		if (!Array.isArray(tokens)) throw new TypeError("render expects token array as first argument");
		if (tokens.length === 1) return this.renderSingleToken(tokens, tokens[0], options, env);
		const merged = this.mergeOptions(options);
		const envRef = env ?? {};
		const rules = this.rules;
		const xhtmlOut = merged.xhtmlOut === true;
		let textRule;
		let textSpecialRule;
		let softbreakRule;
		let hardbreakRule;
		let htmlInlineRule;
		let codeInlineRule;
		let inlineBreak = "";
		let softbreak = "";
		let inlineFastPathReady = false;
		let result = "";
		for (let i = 0; i < tokens.length; i++) {
			const token = tokens[i];
			const type = token.type;
			const prefix = i > 0 && tokens[i - 1].hidden ? "\n" : "";
			if (type === "list_item_open" && (!token.attrs || token.attrs.length === 0) && i + 3 < tokens.length) {
				const paragraphOpen = tokens[i + 1];
				const inlineToken = tokens[i + 2];
				const paragraphClose = tokens[i + 3];
				if (paragraphOpen.type === "paragraph_open" && paragraphOpen.hidden && inlineToken.type === "inline" && paragraphClose.type === "paragraph_close" && paragraphClose.hidden) {
					result += `${prefix}<li>${this.renderInlineTokens(inlineToken.children || [], merged, envRef)}`;
					i += 3;
					continue;
				}
			}
			if (i + 2 < tokens.length) {
				const inlineToken = tokens[i + 1];
				const closeToken = tokens[i + 2];
				if (inlineToken.type === "inline" && closeToken.nesting === -1 && closeToken.tag === token.tag && !closeToken.hidden) {
					const open = renderFastBlockOpenWithInline(token, prefix);
					if (open !== null) {
						result += `${open + this.renderInlineTokens(inlineToken.children || [], merged, envRef)}</${token.tag}>\n`;
						i += 2;
						continue;
					}
				}
			}
			if (type === "inline") {
				const children = token.children || [];
				if (children.length === 1) {
					if (!inlineFastPathReady) {
						textRule = rules.text;
						textSpecialRule = rules.text_special;
						softbreakRule = rules.softbreak;
						hardbreakRule = rules.hardbreak;
						htmlInlineRule = rules.html_inline;
						codeInlineRule = rules.code_inline;
						inlineBreak = merged.xhtmlOut ? "<br />\n" : "<br>\n";
						softbreak = merged.breaks ? inlineBreak : "\n";
						inlineFastPathReady = true;
					}
					const child = children[0];
					switch (child.type) {
						case "text":
							if (textRule === defaultRules.text) {
								result += escapeHtml(child.content);
								continue;
							}
							break;
						case "text_special":
							if (textSpecialRule === defaultRules.text_special) {
								result += escapeHtml(child.content);
								continue;
							}
							break;
						case "softbreak":
							if (softbreakRule === defaultRules.softbreak) {
								result += softbreak;
								continue;
							}
							break;
						case "hardbreak":
							if (hardbreakRule === defaultRules.hardbreak) {
								result += inlineBreak;
								continue;
							}
							break;
						case "html_inline":
							if (htmlInlineRule === defaultRules.html_inline) {
								result += child.content;
								continue;
							}
							break;
						case "code_inline":
							if (codeInlineRule === defaultRules.code_inline) {
								result += renderCodeInlineToken(child);
								continue;
							}
							break;
						default: break;
					}
				}
				result += this.renderInlineTokens(children, merged, envRef);
				continue;
			}
			const rule = rules[type];
			if (!rule) {
				const attrs = token.attrs;
				if (!token.hidden) {
					if (!attrs || attrs.length === 0) switch (type) {
						case "hr":
							result += xhtmlOut ? "<hr />\n" : "<hr>\n";
							continue;
						case "heading_open":
							result += `<${token.tag}>`;
							continue;
						case "heading_close":
							result += `</${token.tag}>\n`;
							continue;
						case "paragraph_open":
							result += `${prefix}<p>`;
							continue;
						case "paragraph_close":
							result += "</p>\n";
							continue;
						case "list_item_open": {
							const nextToken = tokens[i + 1];
							result += prefix + (nextToken && (nextToken.type === "inline" || nextToken.hidden || nextToken.nesting === -1 && nextToken.tag === "li") ? "<li>" : "<li>\n");
							continue;
						}
						case "list_item_close":
							result += "</li>\n";
							continue;
						case "bullet_list_open":
							result += `${prefix}<ul>\n`;
							continue;
						case "bullet_list_close":
							result += "</ul>\n";
							continue;
						case "blockquote_open":
							result += prefix + (tokens[i + 1] && tokens[i + 1].nesting === -1 && tokens[i + 1].tag === "blockquote" ? "<blockquote>" : "<blockquote>\n");
							continue;
						case "blockquote_close":
							result += "</blockquote>\n";
							continue;
						case "ordered_list_open":
							result += `${prefix}<ol>\n`;
							continue;
						case "ordered_list_close":
							result += "</ol>\n";
							continue;
						case "table_open":
							result += `${prefix}<table>\n`;
							continue;
						case "table_close":
							result += "</table>\n";
							continue;
						case "thead_open":
							result += `${prefix}<thead>\n`;
							continue;
						case "thead_close":
							result += "</thead>\n";
							continue;
						case "tbody_open":
							result += `${prefix}<tbody>\n`;
							continue;
						case "tbody_close":
							result += "</tbody>\n";
							continue;
						case "tr_open":
							result += `${prefix}<tr>\n`;
							continue;
						case "tr_close":
							result += "</tr>\n";
							continue;
						case "td_open":
							result += `${prefix}<td>`;
							continue;
						case "td_close":
							result += "</td>\n";
							continue;
						case "th_open":
							result += `${prefix}<th>`;
							continue;
						case "th_close":
							result += "</th>\n";
							continue;
						default: break;
					}
					else if (attrs.length === 1) {
						const attr = attrs[0];
						if (type === "ordered_list_open" && attr[0] === "start") {
							result += `${prefix}<ol start="${escapeHtml(attr[1])}">\n`;
							continue;
						}
						if (type === "td_open" && attr[0] === "style") {
							result += `${prefix}<td style="${escapeHtml(attr[1])}">`;
							continue;
						}
						if (type === "th_open" && attr[0] === "style") {
							result += `${prefix}<th style="${escapeHtml(attr[1])}">`;
							continue;
						}
					}
				}
				result += this.renderToken(tokens, i, merged);
				continue;
			}
			if (type === "code_block" && rule === defaultRules.code_block) {
				result += renderCodeBlockToken(token);
				continue;
			}
			if (type === "fence" && rule === defaultRules.fence) {
				result += renderFenceSyncToken(token, merged, this);
				continue;
			}
			if (type === "html_block" && rule === defaultRules.html_block) {
				result += token.content;
				continue;
			}
			const rendered = rule(tokens, i, merged, envRef, this);
			if (typeof rendered === "string") result += rendered;
			else result += ensureSyncResult(rendered, token.type);
		}
		return result;
	}
	async renderAsync(tokens, options, env) {
		if (!Array.isArray(tokens)) throw new TypeError("render expects token array as first argument");
		const merged = this.mergeOptions(options);
		const envRef = env ?? {};
		const rules = this.rules;
		let result = "";
		for (let i = 0; i < tokens.length; i++) {
			const token = tokens[i];
			if (token.type === "inline") {
				result += await this.renderInlineTokensAsync(token.children || [], merged, envRef);
				continue;
			}
			const rule = rules[token.type];
			if (rule) result += await resolveResult(rule(tokens, i, merged, envRef, this));
			else result += this.renderToken(tokens, i, merged);
		}
		return result;
	}
	renderInline(tokens, options, env) {
		const merged = this.mergeOptions(options);
		const envRef = env ?? {};
		return this.renderInlineTokens(tokens, merged, envRef);
	}
	async renderInlineAsync(tokens, options, env) {
		const merged = this.mergeOptions(options);
		const envRef = env ?? {};
		return this.renderInlineTokensAsync(tokens, merged, envRef);
	}
	renderInlineAsText(tokens, options, env) {
		const merged = this.mergeOptions(options);
		const envRef = env ?? {};
		return this.renderInlineAsTextInternal(tokens, merged, envRef);
	}
	renderAttrs(token) {
		return renderAttrsFromList(token.attrs);
	}
	renderToken(tokens, idx, options) {
		const token = tokens[idx];
		if (token.hidden) return "";
		const block = token.block;
		const nesting = token.nesting;
		const tag = token.tag;
		const attrs = token.attrs;
		let needLineFeed = false;
		if (block) {
			needLineFeed = true;
			if (nesting === 1 && idx + 1 < tokens.length) {
				const nextToken = tokens[idx + 1];
				if (nextToken.type === "inline" || nextToken.hidden) needLineFeed = false;
				else if (nextToken.nesting === -1 && nextToken.tag === tag) needLineFeed = false;
			}
		}
		const prefix = block && nesting !== -1 && idx > 0 && tokens[idx - 1].hidden ? "\n" : "";
		const suffix = needLineFeed ? ">\n" : ">";
		if (!attrs || attrs.length === 0) {
			if (nesting === 0) {
				if (options.xhtmlOut) return `${prefix}<${tag} /${suffix}`;
				return `${prefix}<${tag}${suffix}`;
			}
			if (nesting === -1) return `${prefix}</${tag}${suffix}`;
			return `${prefix}<${tag}${suffix}`;
		}
		let result = prefix + (nesting === -1 ? "</" : "<") + tag + renderAttrsFromList(attrs);
		if (nesting === 0 && options.xhtmlOut) result += " /";
		return result + suffix;
	}
	mergeOptions(overrides) {
		const base = this.normalizedBase;
		if (!overrides) return base;
		if (overrides.highlight === base.highlight && overrides.langPrefix === base.langPrefix && overrides.xhtmlOut === base.xhtmlOut && overrides.breaks === base.breaks) return base;
		let merged = null;
		const ensureMerged = () => {
			if (!merged) merged = { ...base };
			return merged;
		};
		if (hasOwn.call(overrides, "highlight") && overrides.highlight !== base.highlight) ensureMerged().highlight = overrides.highlight;
		if (hasOwn.call(overrides, "langPrefix")) {
			const value = overrides.langPrefix;
			if (value !== base.langPrefix) ensureMerged().langPrefix = value;
		}
		if (hasOwn.call(overrides, "xhtmlOut")) {
			const value = overrides.xhtmlOut;
			if (value !== base.xhtmlOut) ensureMerged().xhtmlOut = value;
		}
		if (hasOwn.call(overrides, "breaks")) {
			const value = overrides.breaks;
			if (value !== base.breaks) ensureMerged().breaks = value;
		}
		return merged || base;
	}
	buildNormalizedBase() {
		return Object.freeze({
			...DEFAULT_RENDERER_OPTIONS,
			...this.baseOptions
		});
	}
	renderSingleToken(tokens, token, options, env) {
		const rules = this.rules;
		const type = token.type;
		if (type === "code_block" && rules.code_block === defaultRules.code_block) return renderCodeBlockToken(token);
		if (type === "html_block" && rules.html_block === defaultRules.html_block) return token.content;
		const merged = this.mergeOptions(options);
		const envRef = env ?? {};
		if (type === "inline") return this.renderInlineTokens(token.children || [], merged, envRef);
		const rule = rules[type];
		if (!rule) return token.block ? this.renderToken(tokens, 0, merged) : renderFlatToken(token, merged.xhtmlOut === true);
		if (type === "fence" && rule === defaultRules.fence) return renderFenceSyncToken(token, merged, this);
		const rendered = rule(tokens, 0, merged, envRef, this);
		if (typeof rendered === "string") return rendered;
		return ensureSyncResult(rendered, type);
	}
	renderInlineTokens(tokens, options, env) {
		if (!tokens || tokens.length === 0) return "";
		const rules = this.rules;
		const textRule = rules.text;
		const textSpecialRule = rules.text_special;
		const softbreakRule = rules.softbreak;
		const hardbreakRule = rules.hardbreak;
		const htmlInlineRule = rules.html_inline;
		const codeInlineRule = rules.code_inline;
		const linkOpenRule = rules.link_open;
		const linkCloseRule = rules.link_close;
		const emOpenRule = rules.em_open;
		const emCloseRule = rules.em_close;
		const strongOpenRule = rules.strong_open;
		const strongCloseRule = rules.strong_close;
		const xhtmlOut = options.xhtmlOut === true;
		const inlineBreak = xhtmlOut ? "<br />\n" : "<br>\n";
		const softbreak = options.breaks ? inlineBreak : "\n";
		if (tokens.length === 1) return renderSingleInlineTokenSync(tokens, options, env, this, rules, inlineBreak, softbreak);
		let result = "";
		for (let i = 0; i < tokens.length; i++) {
			const token = tokens[i];
			if (token.type === "link_open" && !linkOpenRule && !linkCloseRule && i + 2 < tokens.length) {
				const bodyToken = tokens[i + 1];
				if (tokens[i + 2].type === "link_close" && canUseLinkInlineFastPath(bodyToken)) {
					const renderedLink = `${renderLinkOpenToken(token) + renderSingleInlineTokenSync([bodyToken], options, env, this, rules, inlineBreak, softbreak)}</a>`;
					if (softbreakRule === defaultRules.softbreak && i + 3 < tokens.length && tokens[i + 3].type === "softbreak") {
						result += renderedLink + softbreak;
						i += 3;
						continue;
					}
					result += renderedLink;
					i += 2;
					continue;
				}
			}
			if (token.type === "link_open" && !linkOpenRule && !linkCloseRule && i + 1 < tokens.length) {
				if (tokens[i + 1].type === "link_close") {
					result += `${renderLinkOpenToken(token)}</a>`;
					i += 1;
					continue;
				}
			}
			if (token.type === "em_open" && !emOpenRule && !emCloseRule && i + 2 < tokens.length) {
				const bodyToken = tokens[i + 1];
				if (tokens[i + 2].type === "em_close" && canUseWrappedInlineFastPath(bodyToken)) {
					result += `<em>${renderSingleInlineTokenSync([bodyToken], options, env, this, rules, inlineBreak, softbreak)}</em>`;
					i += 2;
					continue;
				}
			}
			if (token.type === "strong_open" && !strongOpenRule && !strongCloseRule && i + 2 < tokens.length) {
				const bodyToken = tokens[i + 1];
				if (tokens[i + 2].type === "strong_close" && canUseWrappedInlineFastPath(bodyToken)) {
					result += `<strong>${renderSingleInlineTokenSync([bodyToken], options, env, this, rules, inlineBreak, softbreak)}</strong>`;
					i += 2;
					continue;
				}
			}
			switch (token.type) {
				case "text":
					if (textRule === defaultRules.text) {
						const escaped = token.content.length === 0 ? "" : escapeHtml(token.content);
						if (htmlInlineRule === defaultRules.html_inline && i + 1 < tokens.length && tokens[i + 1].type === "html_inline") {
							result += escaped + tokens[++i].content;
							while (i + 1 < tokens.length && tokens[i + 1].type === "html_inline") result += tokens[++i].content;
							continue;
						}
						result += escaped;
						continue;
					}
					break;
				case "text_special":
					if (textSpecialRule === defaultRules.text_special) {
						if (token.content.length !== 0) result += escapeHtml(token.content);
						continue;
					}
					break;
				case "softbreak":
					if (softbreakRule === defaultRules.softbreak) {
						result += softbreak;
						continue;
					}
					break;
				case "hardbreak":
					if (hardbreakRule === defaultRules.hardbreak) {
						result += inlineBreak;
						continue;
					}
					break;
				case "html_inline":
					if (htmlInlineRule === defaultRules.html_inline) {
						result += token.content;
						while (i + 1 < tokens.length && tokens[i + 1].type === "html_inline") result += tokens[++i].content;
						continue;
					}
					break;
				case "code_inline":
					if (codeInlineRule === defaultRules.code_inline) {
						result += renderCodeInlineToken(token);
						continue;
					}
					break;
				default: break;
			}
			const rule = rules[token.type];
			if (!rule) {
				result += token.block ? this.renderToken(tokens, i, options) : renderFlatToken(token, xhtmlOut);
				continue;
			}
			const rendered = rule(tokens, i, options, env, this);
			if (typeof rendered === "string") result += rendered;
			else result += ensureSyncResult(rendered, token.type);
		}
		return result;
	}
	async renderInlineTokensAsync(tokens, options, env) {
		if (!tokens || tokens.length === 0) return "";
		const rules = this.rules;
		let result = "";
		for (let i = 0; i < tokens.length; i++) {
			const rule = rules[tokens[i].type];
			if (rule) result += await resolveResult(rule(tokens, i, options, env, this));
			else result += this.renderToken(tokens, i, options);
		}
		return result;
	}
	renderInlineAsTextInternal(tokens, options, env) {
		if (!tokens || tokens.length === 0) return "";
		let output = "";
		for (let i = 0; i < tokens.length; i++) {
			const token = tokens[i];
			switch (token.type) {
				case "text":
				case "text_special":
					output += token.content;
					break;
				case "image":
					output += this.renderInlineAsTextInternal(token.children || [], options, env);
					break;
				case "html_inline":
				case "html_block":
					output += token.content;
					break;
				case "softbreak":
				case "hardbreak":
					output += "\n";
					break;
				default: break;
			}
		}
		return output;
	}
};
var renderer_default = Renderer;

//#endregion
//#region src/stream/parser.ts
const EMPTY_TOKENS = [];
function makeEmptyStats() {
	return {
		total: 0,
		cacheHits: 0,
		appendHits: 0,
		unboundedAppendHits: 0,
		tailHits: 0,
		fullParses: 0,
		resets: 0,
		chunkedParses: 0,
		lastMode: "idle"
	};
}
var StreamParser = class {
	core;
	cache = null;
	stats = makeEmptyStats();
	MIN_SIZE_FOR_OPTIMIZATION = 1e3;
	DEFAULT_SKIP_CACHE_CHARS = 1e6;
	DEFAULT_SKIP_CACHE_LINES = 1e5;
	IMPLICIT_STREAM_CHUNK_MIN_CHARS = 16e4;
	MIN_LIST_LINES_FOR_MERGE = 80;
	MIN_LIST_CHARS_FOR_MERGE = 800;
	MIN_TABLE_LINES_FOR_MERGE = 48;
	MIN_TABLE_CHARS_FOR_MERGE = 1200;
	MIN_UNBOUNDED_APPEND_TOTAL_CHARS = 5e5;
	MIN_UNBOUNDED_APPEND_CHARS = 64e3;
	MIN_UNBOUNDED_APPEND_LINES = 700;
	constructor(core) {
		this.core = core;
	}
	reset() {
		this.cache = null;
		this.stats.resets += 1;
		this.stats.lastMode = "reset";
	}
	resetStats() {
		const { resets } = this.stats;
		this.stats = makeEmptyStats();
		this.stats.resets = resets;
	}
	parse(src, env, md) {
		const envProvided = env;
		const cached = this.cache;
		beginParseDiagnostics(envProvided ?? cached?.env);
		if (!cached || envProvided && envProvided !== cached.env) {
			const workingEnv = envProvided ?? {};
			const explicitChunkFallbackSetting$1 = !!md.__explicitStreamChunkFallbackSetting;
			const canImplicitLargeInput$1 = typeof md.__canUseImplicitLargeInputStrategy === "function" ? md.__canUseImplicitLargeInputStrategy() : true;
			const wantsChunking$1 = !!md.options?.streamChunkedFallback;
			const allowImplicitChunk$1 = !explicitChunkFallbackSetting$1 && canImplicitLargeInput$1;
			const chunkedEnabled$1 = wantsChunking$1 || allowImplicitChunk$1;
			const chunkAdaptive$1 = md.options?.streamChunkAdaptive !== false;
			const targetChunks$1 = md.options?.streamChunkTargetChunks ?? 8;
			const chunkSizeCharsCfg$1 = md.options?.streamChunkSizeChars;
			const chunkSizeLinesCfg$1 = md.options?.streamChunkSizeLines;
			const chunkMaxChunksCfg$1 = md.options?.streamChunkMaxChunks;
			const explicitChunkConfig$1 = !!md.__explicitStreamChunkConfig;
			const auto$1 = md.options?.autoTuneChunks !== false;
			const chunkFenceAware$1 = md.options?.streamChunkFenceAware ?? true;
			const skipCacheChars = md.options?.streamSkipCacheAboveChars ?? this.DEFAULT_SKIP_CACHE_CHARS;
			const skipCacheLines = md.options?.streamSkipCacheAboveLines ?? this.DEFAULT_SKIP_CACHE_LINES;
			let srcLineCount;
			let isVeryLargeOneShot = src.length >= skipCacheChars;
			if (!isVeryLargeOneShot && skipCacheLines !== void 0) {
				srcLineCount = countLines(src);
				isVeryLargeOneShot = srcLineCount >= skipCacheLines;
			}
			if (isVeryLargeOneShot) {
				const parsed$2 = this.parseFullDocument(src, workingEnv, md, srcLineCount, false);
				this.stats.total += 1;
				this.stats.fullParses += 1;
				this.stats.lastMode = "full";
				setStrategyDiagnostics(workingEnv, {
					area: "stream",
					path: "stream-full",
					reason: "skip-cache-large-one-shot",
					unbounded: !!getParseDiagnostics(workingEnv)?.unbounded
				});
				return parsed$2.tokens;
			} else if (chunkedEnabled$1) {
				const clamp = (v, lo, hi) => v < lo ? lo : v > hi ? hi : v;
				if (srcLineCount === void 0) srcLineCount = countLines(src);
				const recommendation = auto$1 && !explicitChunkConfig$1 ? recommendStreamChunkStrategy(src.length, srcLineCount, md.options) : null;
				const useChars = recommendation?.maxChunkChars ?? (chunkAdaptive$1 ? clamp(Math.ceil(src.length / targetChunks$1), 8e3, 64e3) : chunkSizeCharsCfg$1 ?? 1e4);
				const useLines = recommendation?.maxChunkLines ?? (chunkAdaptive$1 ? clamp(Math.ceil(srcLineCount / targetChunks$1), 150, 700) : chunkSizeLinesCfg$1 ?? 200);
				const useMaxChunks = recommendation?.maxChunks ?? (chunkAdaptive$1 ? clamp(Math.ceil(src.length / 64e3), targetChunks$1, 32) : chunkMaxChunksCfg$1);
				const hasTrailingNewline = src.length > 0 && src.charCodeAt(src.length - 1) === 10;
				const shouldAutoChunk = allowImplicitChunk$1 && src.length >= this.IMPLICIT_STREAM_CHUNK_MIN_CHARS && recommendation?.strategy !== "plain";
				if ((wantsChunking$1 || shouldAutoChunk) && (src.length >= useChars * 2 || srcLineCount >= useLines * 2) && hasTrailingNewline) {
					const tokens = chunkedParse(md, src, workingEnv, {
						maxChunkChars: useChars,
						maxChunkLines: useLines,
						fenceAware: recommendation?.fenceAware ?? chunkFenceAware$1,
						maxChunks: useMaxChunks
					});
					this.cache = {
						src,
						tokens,
						env: workingEnv,
						lineCount: srcLineCount,
						lastSegment: void 0,
						globalStateReason: detectGlobalMarkdownState(src)
					};
					this.updateCacheLineCount(this.cache, srcLineCount);
					this.recordChunkedParseResult(workingEnv, wantsChunking$1 ? "explicit-initial-large-doc" : "default-initial-large-doc");
					return tokens;
				}
			}
			const parsed$1 = this.parseFullDocument(src, workingEnv, md, srcLineCount);
			srcLineCount = parsed$1.lineCount;
			this.cache = {
				src,
				tokens: parsed$1.tokens,
				env: workingEnv,
				lineCount: srcLineCount,
				lastSegment: void 0,
				globalStateReason: detectGlobalMarkdownState(src)
			};
			this.updateCacheLineCount(this.cache, srcLineCount);
			this.stats.total += 1;
			this.stats.fullParses += 1;
			this.stats.lastMode = "full";
			setStrategyDiagnostics(workingEnv, {
				area: "stream",
				path: "stream-full",
				reason: "initial-parse",
				unbounded: !!getParseDiagnostics(workingEnv)?.unbounded
			});
			return parsed$1.tokens;
		}
		if (src === cached.src) {
			this.stats.total += 1;
			this.stats.cacheHits += 1;
			this.stats.lastMode = "cache";
			setStrategyDiagnostics(cached.env, {
				area: "stream",
				path: "stream-cache",
				reason: "same-source"
			});
			return cached.tokens;
		}
		let cachedGlobalStateReason = cached.globalStateReason;
		if (cachedGlobalStateReason === void 0) {
			cachedGlobalStateReason = detectGlobalMarkdownState(cached.src);
			cached.globalStateReason = cachedGlobalStateReason;
		}
		const currentGlobalStateReason = detectGlobalMarkdownState(src);
		const nextGlobalStateReason = cachedGlobalStateReason || currentGlobalStateReason;
		if (nextGlobalStateReason) {
			const fallbackEnv$1 = envProvided ?? cached.env;
			resetKnownGlobalMarkdownState(fallbackEnv$1);
			const parsed$1 = this.parseFullDocument(src, fallbackEnv$1, md);
			const nextTokens$1 = parsed$1.tokens;
			const lineCount = parsed$1.lineCount;
			this.cache = {
				src,
				tokens: nextTokens$1,
				env: fallbackEnv$1,
				lineCount,
				lastSegment: void 0,
				globalStateReason: currentGlobalStateReason
			};
			this.updateCacheLineCount(this.cache, lineCount);
			this.stats.total += 1;
			this.stats.fullParses += 1;
			this.stats.lastMode = "full";
			setStrategyDiagnostics(fallbackEnv$1, {
				area: "stream",
				path: "stream-full",
				reason: `global-state:${nextGlobalStateReason}`,
				unbounded: !!getParseDiagnostics(fallbackEnv$1)?.unbounded
			});
			return nextTokens$1;
		}
		const threshold = md.options?.streamOptimizationMinSize ?? this.MIN_SIZE_FOR_OPTIMIZATION;
		if (cached.src.length < threshold && src.length < threshold * 1.5 && !src.startsWith(cached.src)) {
			const fallbackEnv$1 = envProvided ?? cached.env;
			const parsed$1 = this.parseFullDocument(src, fallbackEnv$1, md);
			const nextTokens$1 = parsed$1.tokens;
			const lineCount = parsed$1.lineCount;
			this.cache = {
				src,
				tokens: nextTokens$1,
				env: fallbackEnv$1,
				lineCount,
				lastSegment: void 0,
				globalStateReason: detectGlobalMarkdownState(src)
			};
			this.updateCacheLineCount(this.cache, lineCount);
			this.stats.total += 1;
			this.stats.fullParses += 1;
			this.stats.lastMode = "full";
			setStrategyDiagnostics(fallbackEnv$1, {
				area: "stream",
				path: "stream-full",
				reason: "small-non-append",
				unbounded: !!getParseDiagnostics(fallbackEnv$1)?.unbounded
			});
			return nextTokens$1;
		}
		const appended = this.getAppendedSegment(cached.src, src);
		if (appended && !this.shouldPreferTailReparseForAppend(cached)) {
			const cachedLineCount = cached.lineCount ?? countLines(cached.src);
			let ctxLines = 3;
			if (appended.length > 5e3) ctxLines = 8;
			else if (appended.length > 1e3) ctxLines = 6;
			else if (appended.length > 200) ctxLines = 4;
			ctxLines = Math.min(ctxLines, cachedLineCount);
			let appendedState = null;
			const ctxStrategy = md.options?.streamContextParseStrategy ?? "chars";
			const CONTEXT_PARSE_MIN_CHARS = md.options?.streamContextParseMinChars ?? 200;
			const CONTEXT_PARSE_MIN_LINES = md.options?.streamContextParseMinLines ?? 2;
			function appendedHasBlockConstructs(s) {
				const len = s.length;
				let lineStart = 0;
				while (lineStart <= len) {
					let lineEnd = s.indexOf("\n", lineStart);
					if (lineEnd === -1) lineEnd = len;
					const hasLineBreak = lineEnd < len;
					let p = lineStart;
					let indent = 0;
					while (p < lineEnd) {
						const c = s.charCodeAt(p);
						if (c === 32) {
							indent++;
							p++;
							if (indent >= 4) return true;
							continue;
						}
						if (c === 9) {
							indent += 4 - indent % 4;
							p++;
							if (indent >= 4) return true;
							continue;
						}
						break;
					}
					if (p < lineEnd) {
						const ch = s.charCodeAt(p);
						switch (ch) {
							case 35: {
								let q = p;
								while (q < lineEnd && s.charCodeAt(q) === 35) q++;
								const runLen = q - p;
								if (runLen > 0 && runLen <= 6) {
									if (q < lineEnd) {
										const next = s.charCodeAt(q);
										if (next === 32 || next === 9 || next === 13) return true;
									} else if (q === lineEnd && hasLineBreak) return true;
								}
								break;
							}
							case 62: {
								const nextPos = p + 1;
								if (nextPos < lineEnd) {
									const next = s.charCodeAt(nextPos);
									if (next === 32 || next === 9 || next === 13) return true;
								} else if (nextPos === lineEnd && hasLineBreak) return true;
								break;
							}
							case 45:
							case 42:
							case 43: {
								const nextPos = p + 1;
								if (nextPos < lineEnd) {
									const next = s.charCodeAt(nextPos);
									if (next === 32 || next === 9 || next === 13) return true;
								} else if (nextPos === lineEnd && hasLineBreak) return true;
								break;
							}
							case 96:
							case 126: {
								let q = p;
								while (q < lineEnd && s.charCodeAt(q) === ch) q++;
								if (q - p >= 3) return true;
								break;
							}
							default:
								if (ch >= 48 && ch <= 57) {
									let q = p + 1;
									while (q < lineEnd) {
										const d = s.charCodeAt(q);
										if (d < 48 || d > 57) break;
										q++;
									}
									if (q < lineEnd && s.charCodeAt(q) === 46) {
										const nextPos = q + 1;
										if (nextPos < lineEnd) {
											const next = s.charCodeAt(nextPos);
											if (next === 32 || next === 9 || next === 13) return true;
										} else if (nextPos === lineEnd && hasLineBreak) return true;
									}
								}
								break;
						}
					}
					if (lineEnd === len) break;
					lineStart = lineEnd + 1;
				}
				return false;
			}
			let appendedLineCount = null;
			const getAppendedLineCount = () => {
				if (appendedLineCount === null) appendedLineCount = countLines(appended);
				return appendedLineCount;
			};
			const canDirectParseAppend = this.canDirectlyParseAppend(cached);
			const useUnboundedAppend = canDirectParseAppend && this.shouldUseUnboundedAppend(src, cached, appended);
			let shouldAttemptContext = false;
			if (!canDirectParseAppend) switch (ctxStrategy) {
				case "lines":
					shouldAttemptContext = getAppendedLineCount() >= CONTEXT_PARSE_MIN_LINES;
					break;
				case "constructs":
					if (appended.length >= CONTEXT_PARSE_MIN_CHARS) {
						shouldAttemptContext = true;
						break;
					}
					if (appendedHasBlockConstructs(appended)) {
						shouldAttemptContext = true;
						break;
					}
					shouldAttemptContext = getAppendedLineCount() >= CONTEXT_PARSE_MIN_LINES;
					break;
				case "chars":
				default: shouldAttemptContext = appended.length >= CONTEXT_PARSE_MIN_CHARS;
			}
			if (ctxLines > 0 && shouldAttemptContext) {
				const ctxSrc = this.getTailLines(cached.src, ctxLines) + appended;
				try {
					const ctxTokens = this.core.parse(ctxSrc, cached.env, md).tokens;
					const idx = ctxTokens.findIndex((t) => t.map && typeof t.map[1] === "number" && t.map[1] > ctxLines);
					if (idx !== -1) {
						const appendedTokens = ctxTokens.slice(idx);
						const shiftBy = cachedLineCount - ctxLines;
						if (shiftBy !== 0) this.shiftTokenLines(appendedTokens, shiftBy);
						appendedState = { tokens: appendedTokens };
					}
				} catch {
					appendedState = null;
				}
			} else appendedState = null;
			if (!appendedState) {
				const lineOffset = cachedLineCount;
				if (useUnboundedAppend) {
					appendedState = { tokens: parseStringUnbounded(md, appended, cached.env, { mode: "stream" }) };
					if (lineOffset > 0) this.shiftTokenLines(appendedState.tokens, lineOffset);
				} else {
					const simpleState = this.core.parse(appended, cached.env, md);
					if (lineOffset > 0) this.shiftTokenLines(simpleState.tokens, lineOffset);
					appendedState = simpleState;
				}
			}
			if (cached.tokens.length > 0 && appendedState.tokens.length > 0) {
				const lastCached = cached.tokens[cached.tokens.length - 1];
				const firstApp = appendedState.tokens[0];
				try {
					if (lastCached.type === "inline" && firstApp.type === "inline") {
						if (firstApp.children && firstApp.children.length > 0) {
							if (!lastCached.children) lastCached.children = [];
							this.appendTokens(lastCached.children, firstApp.children);
						}
						lastCached.content = (lastCached.content || "") + (firstApp.content || "");
						appendedState.tokens.shift();
					}
				} catch {}
			}
			const appendStart = cached.tokens.length;
			if (appendedState.tokens.length > 0) {
				const cachedTail = cached.tokens;
				const a = appendedState.tokens;
				const maxCheck = Math.min(cachedTail.length, a.length);
				function attrsEqual(a$1, b) {
					if (!a$1 && !b) return true;
					if (!a$1 || !b || a$1.length !== b.length) return false;
					for (let i = 0; i < a$1.length; i++) if (a$1[i][0] !== b[i][0] || a$1[i][1] !== b[i][1]) return false;
					return true;
				}
				function childrenEqual(a$1, b) {
					if (!a$1 && !b) return true;
					if (!a$1 || !b || a$1.length !== b.length) return false;
					for (let i = 0; i < a$1.length; i++) if (!tokenEquals(a$1[i], b[i])) return false;
					return true;
				}
				function tokenEquals(x, y) {
					if (!x || !y) return false;
					if (x.type !== y.type) return false;
					const xMap = x.map;
					const yMap = y.map;
					if (!!xMap !== !!yMap) return false;
					if (xMap && yMap && (xMap[0] !== yMap[0] || xMap[1] !== yMap[1])) return false;
					if (x.tag !== y.tag || x.nesting !== y.nesting) return false;
					if (x.markup !== y.markup || x.info !== y.info) return false;
					if (x.block !== y.block || x.hidden !== y.hidden) return false;
					if (!attrsEqual(x.attrs, y.attrs)) return false;
					if (!childrenEqual(x.children, y.children)) return false;
					if (x.type === "inline") return (x.content || "") === (y.content || "");
					return (x.content || "") === (y.content || "");
				}
				let dup = 0;
				for (let n = maxCheck; n > 0; n--) {
					let ok = true;
					for (let i = 0; i < n; i++) {
						const tailToken = cachedTail[cachedTail.length - n + i];
						const prefToken = a[i];
						if (!tokenEquals(tailToken, prefToken)) {
							ok = false;
							break;
						}
					}
					if (ok) {
						dup = n;
						break;
					}
				}
				if (dup > 0) a.splice(0, dup);
				if (a.length > 0) this.appendTokens(cached.tokens, a);
			}
			cached.src = src;
			cached.globalStateReason = detectGlobalMarkdownState(src);
			cached.lineCount = cachedLineCount + (appendedLineCount ?? countLines(appended));
			if (cached.tokens.length > appendStart) {
				const appendedLastSegment = this.getLastSegment(cached.tokens.slice(appendStart), src);
				if (appendedLastSegment) cached.lastSegment = {
					tokenStart: appendStart + appendedLastSegment.tokenStart,
					tokenEnd: appendStart + appendedLastSegment.tokenEnd,
					lineStart: appendedLastSegment.lineStart,
					lineEnd: appendedLastSegment.lineEnd,
					srcOffset: appendedLastSegment.srcOffset
				};
				else cached.lastSegment = void 0;
			} else cached.lastSegment = void 0;
			this.stats.total += 1;
			this.stats.appendHits += 1;
			if (useUnboundedAppend) this.stats.unboundedAppendHits = (this.stats.unboundedAppendHits || 0) + 1;
			this.stats.lastMode = "append";
			setStrategyDiagnostics(cached.env, {
				area: "stream",
				path: useUnboundedAppend ? "stream-unbounded-append" : "stream-append",
				reason: useUnboundedAppend ? "large-delta" : "safe-append",
				unbounded: useUnboundedAppend
			});
			return cached.tokens;
		}
		const fallbackEnv = envProvided ?? cached.env;
		const tailReparsed = this.tryTailSegmentReparse(src, cached, fallbackEnv, md);
		if (tailReparsed) {
			this.stats.total += 1;
			this.stats.tailHits += 1;
			this.stats.lastMode = "tail";
			setStrategyDiagnostics(fallbackEnv, {
				area: "stream",
				path: "stream-tail",
				reason: "tail-reparse"
			});
			return tailReparsed;
		}
		const explicitChunkFallbackSetting = !!md.__explicitStreamChunkFallbackSetting;
		const canImplicitLargeInput = typeof md.__canUseImplicitLargeInputStrategy === "function" ? md.__canUseImplicitLargeInputStrategy() : true;
		const wantsChunking = !!md.options?.streamChunkedFallback;
		const allowImplicitChunk = !explicitChunkFallbackSetting && !appended && canImplicitLargeInput;
		const chunkedEnabled = wantsChunking || allowImplicitChunk;
		const chunkAdaptive = md.options?.streamChunkAdaptive !== false;
		const targetChunks = md.options?.streamChunkTargetChunks ?? 8;
		const chunkSizeCharsCfg = md.options?.streamChunkSizeChars;
		const chunkSizeLinesCfg = md.options?.streamChunkSizeLines;
		const chunkMaxChunksCfg = md.options?.streamChunkMaxChunks;
		const explicitChunkConfig = !!md.__explicitStreamChunkConfig;
		const auto = md.options?.autoTuneChunks !== false;
		const chunkFenceAware = md.options?.streamChunkFenceAware ?? true;
		let srcLineCount2 = appended && cached.lineCount !== void 0 ? cached.lineCount + countLines(appended) : void 0;
		if (chunkedEnabled) {
			if (srcLineCount2 === void 0) srcLineCount2 = countLines(src);
			const clamp = (v, lo, hi) => v < lo ? lo : v > hi ? hi : v;
			const recommendation = auto && !explicitChunkConfig ? recommendStreamChunkStrategy(src.length, srcLineCount2, md.options) : null;
			const useChars = recommendation?.maxChunkChars ?? (chunkAdaptive ? clamp(Math.ceil(src.length / targetChunks), 8e3, 64e3) : chunkSizeCharsCfg ?? 1e4);
			const useLines = recommendation?.maxChunkLines ?? (chunkAdaptive ? clamp(Math.ceil(srcLineCount2 / targetChunks), 150, 700) : chunkSizeLinesCfg ?? 200);
			const useMaxChunks = recommendation?.maxChunks ?? (chunkAdaptive ? clamp(Math.ceil(src.length / 64e3), targetChunks, 32) : chunkMaxChunksCfg);
			const hasTrailingNewline2 = src.length > 0 && src.charCodeAt(src.length - 1) === 10;
			const shouldAutoChunk = allowImplicitChunk && src.length >= this.IMPLICIT_STREAM_CHUNK_MIN_CHARS && recommendation?.strategy !== "plain";
			if ((wantsChunking || shouldAutoChunk) && (src.length >= useChars * 2 || srcLineCount2 >= useLines * 2) && hasTrailingNewline2) {
				const tokens = chunkedParse(md, src, fallbackEnv, {
					maxChunkChars: useChars,
					maxChunkLines: useLines,
					fenceAware: recommendation?.fenceAware ?? chunkFenceAware,
					maxChunks: useMaxChunks
				});
				this.cache = {
					src,
					tokens,
					env: fallbackEnv,
					lineCount: srcLineCount2,
					lastSegment: void 0,
					globalStateReason: detectGlobalMarkdownState(src)
				};
				this.updateCacheLineCount(this.cache, srcLineCount2);
				this.recordChunkedParseResult(fallbackEnv, wantsChunking ? "explicit-fallback-large-doc" : "default-fallback-large-doc");
				return tokens;
			}
		}
		const parsed = this.parseFullDocument(src, fallbackEnv, md, srcLineCount2);
		const nextTokens = parsed.tokens;
		srcLineCount2 = parsed.lineCount;
		this.cache = {
			src,
			tokens: nextTokens,
			env: fallbackEnv,
			lineCount: srcLineCount2,
			lastSegment: void 0,
			globalStateReason: detectGlobalMarkdownState(src)
		};
		this.updateCacheLineCount(this.cache, srcLineCount2);
		this.stats.total += 1;
		this.stats.fullParses += 1;
		this.stats.lastMode = "full";
		setStrategyDiagnostics(fallbackEnv, {
			area: "stream",
			path: "stream-full",
			reason: "fallback-full",
			unbounded: !!getParseDiagnostics(fallbackEnv)?.unbounded
		});
		return nextTokens;
	}
	recordChunkedParseResult(env, chunkReason) {
		const chunkInfo = getParseDiagnostics(env)?.chunk;
		const fallbackReason = chunkInfo?.fallback ? String(chunkInfo.fallbackReason || "global-state") : null;
		this.stats.total += 1;
		if (fallbackReason) {
			this.stats.fullParses += 1;
			this.stats.lastMode = "full";
			setStrategyDiagnostics(env, {
				area: "stream",
				path: "stream-full",
				reason: `global-state:${fallbackReason}`,
				unbounded: !!getParseDiagnostics(env)?.unbounded
			});
			return;
		}
		this.stats.chunkedParses = (this.stats.chunkedParses || 0) + 1;
		this.stats.lastMode = "chunked";
		setStrategyDiagnostics(env, {
			area: "stream",
			path: "stream-chunked",
			chunked: true,
			reason: chunkReason
		});
	}
	parseFullDocument(src, env, md, knownLineCount, needLineCount = true) {
		const currentGlobalStateReason = detectGlobalMarkdownState(src);
		if (getKnownGlobalMarkdownState(env)) resetKnownGlobalMarkdownState(env);
		const autoUnboundedDecision = (typeof md.__canUseImplicitLargeInputStrategy === "function" ? md.__canUseImplicitLargeInputStrategy() : true) ? getAutoUnboundedDecision(md, src.length, knownLineCount) : "no";
		if (autoUnboundedDecision === "yes") {
			const tokens = parseStringUnbounded(md, src, env);
			setStrategyDiagnostics(env, {
				area: "stream",
				path: "stream-full",
				reason: "auto-unbounded-char-threshold",
				unbounded: true
			});
			return {
				tokens,
				lineCount: knownLineCount ?? (needLineCount ? countLines(src) : 0)
			};
		}
		let lineCount = knownLineCount;
		if (autoUnboundedDecision === "need-lines") {
			lineCount = countLines(src);
			if (shouldAutoUseUnbounded(md, src.length, lineCount)) {
				const tokens = parseStringUnbounded(md, src, env);
				setStrategyDiagnostics(env, {
					area: "stream",
					path: "stream-full",
					reason: "auto-unbounded-line-threshold",
					unbounded: true
				});
				return {
					tokens,
					lineCount
				};
			}
		}
		if (lineCount === void 0) lineCount = needLineCount ? countLines(src) : 0;
		return {
			tokens: runWithKnownGlobalMarkdownState(env, currentGlobalStateReason, () => {
				return this.core.parse(src, env, md).tokens;
			}),
			lineCount
		};
	}
	shouldUseUnboundedAppend(src, _cached, appended) {
		if (!appended) return false;
		if (src.length < this.MIN_UNBOUNDED_APPEND_TOTAL_CHARS && appended.length < this.MIN_UNBOUNDED_APPEND_CHARS) return false;
		if (appended.length >= this.MIN_UNBOUNDED_APPEND_CHARS) return true;
		return countLines(appended) >= this.MIN_UNBOUNDED_APPEND_LINES;
	}
	getAppendedSegment(prev, next) {
		if (!next.startsWith(prev)) return null;
		if (!prev.endsWith("\n")) return null;
		const segment = next.slice(prev.length);
		if (!segment) return null;
		const segLen = segment.length;
		if (segment.charCodeAt(segLen - 1) !== 10) return null;
		let newlineCount = 0;
		let firstLineBreak = -1;
		for (let i = 0; i < segLen; i++) if (segment.charCodeAt(i) === 10) {
			if (firstLineBreak === -1) firstLineBreak = i;
			newlineCount++;
			if (newlineCount >= 2) break;
		}
		if (newlineCount < 2) return null;
		const trimmedFirstLine = (firstLineBreak === -1 ? segment : segment.slice(0, firstLineBreak)).trim();
		if (trimmedFirstLine.length === 0) return null;
		if (/^[-=]+$/.test(trimmedFirstLine)) {
			const prevWithoutTrailingNewline = prev.slice(0, -1);
			const lastBreak = prevWithoutTrailingNewline.lastIndexOf("\n");
			if (prevWithoutTrailingNewline.slice(lastBreak + 1).trim().length > 0) return null;
		}
		if (this.endsInsideOpenFence(prev)) return null;
		if (this.mayContainReferenceDefinition(segment)) return null;
		return segment;
	}
	tryTailSegmentReparse(src, cached, env, md) {
		const lastSegment = this.ensureLastSegment(cached);
		if (!lastSegment) return null;
		if (lastSegment.srcOffset <= 0 && lastSegment.tokenStart <= 0) return null;
		const stablePrefix = cached.src.slice(0, lastSegment.srcOffset);
		if (!src.startsWith(stablePrefix)) return null;
		const prevTail = cached.src.slice(lastSegment.srcOffset);
		const nextTail = src.slice(lastSegment.srcOffset);
		if (nextTail === prevTail) return null;
		const appended = src.startsWith(cached.src) ? src.slice(cached.src.length) : null;
		if (appended) {
			const merged = this.tryContainerTailAppendMerge(src, cached, env, md, lastSegment, appended);
			if (merged) return merged;
		}
		if (this.mayContainReferenceDefinition(prevTail) || this.mayContainReferenceDefinition(nextTail)) return null;
		try {
			const tailState = this.core.parse(nextTail, env, md);
			const localLastSegment = this.getLastSegment(tailState.tokens, nextTail);
			if (lastSegment.lineStart > 0) this.shiftTokenLines(tailState.tokens, lastSegment.lineStart);
			cached.src = src;
			cached.env = env;
			cached.globalStateReason = detectGlobalMarkdownState(src);
			cached.tokens.length = lastSegment.tokenStart;
			this.appendTokens(cached.tokens, tailState.tokens);
			cached.lineCount = countLines(src);
			if (localLastSegment) cached.lastSegment = {
				tokenStart: lastSegment.tokenStart + localLastSegment.tokenStart,
				tokenEnd: lastSegment.tokenStart + localLastSegment.tokenEnd,
				lineStart: lastSegment.lineStart + localLastSegment.lineStart,
				lineEnd: lastSegment.lineStart + localLastSegment.lineEnd,
				srcOffset: lastSegment.srcOffset + localLastSegment.srcOffset
			};
			else cached.lastSegment = null;
			return cached.tokens;
		} catch {
			return null;
		}
	}
	getTailLines(src, lineCount) {
		if (lineCount <= 0) return "";
		let remaining = lineCount;
		for (let i = src.length - 1; i >= 0; i--) if (src.charCodeAt(i) === 10) {
			remaining--;
			if (remaining === 0) return src.slice(i + 1);
		}
		return src;
	}
	endsInsideOpenFence(text) {
		const WINDOW = 4e3;
		const start = text.length > WINDOW ? text.length - WINDOW : 0;
		const chunk = text.slice(start);
		const len = chunk.length;
		let inFence = null;
		let lineStart = 0;
		while (lineStart <= len) {
			let lineEnd = chunk.indexOf("\n", lineStart);
			if (lineEnd === -1) lineEnd = len;
			let p = lineStart;
			while (p < lineEnd) {
				const c = chunk.charCodeAt(p);
				if (c === 32 || c === 9) p++;
				else break;
			}
			if (p < lineEnd) {
				const ch = chunk.charCodeAt(p);
				if (ch === 96 || ch === 126) {
					let q = p;
					while (q < lineEnd && chunk.charCodeAt(q) === ch) q++;
					const runLen = q - p;
					if (runLen >= 3) {
						if (!inFence) inFence = {
							marker: ch,
							length: runLen
						};
						else if (inFence.marker === ch && runLen >= inFence.length) inFence = null;
					}
				}
			}
			if (lineEnd === len) break;
			lineStart = lineEnd + 1;
		}
		return inFence !== null;
	}
	peek() {
		return this.cache?.tokens ?? EMPTY_TOKENS;
	}
	getStats() {
		return { ...this.stats };
	}
	appendTokens(target, source) {
		for (let i = 0; i < source.length; i++) target.push(source[i]);
	}
	updateCacheLineCount(cache, lineCount) {
		cache.lineCount = lineCount ?? countLines(cache.src);
		cache.lastSegment = void 0;
	}
	ensureLastSegment(cache) {
		if (cache.lastSegment !== void 0) return cache.lastSegment;
		cache.lastSegment = this.getLastSegment(cache.tokens, cache.src);
		return cache.lastSegment;
	}
	getLastSegment(tokens, src) {
		if (tokens.length === 0) return null;
		let lineStart = Number.POSITIVE_INFINITY;
		let lineEnd = -1;
		let depth = 0;
		for (let i = tokens.length - 1; i >= 0; i--) {
			const token = tokens[i];
			if (token.map) {
				if (token.map[0] < lineStart) lineStart = token.map[0];
				if (token.map[1] > lineEnd) lineEnd = token.map[1];
			}
			if (token.nesting < 0) {
				depth += -token.nesting;
				continue;
			}
			if (token.nesting > 0) {
				depth -= token.nesting;
				if (token.level === 0 && depth <= 0) {
					const resolvedStart = Number.isFinite(lineStart) ? lineStart : token.map?.[0] ?? 0;
					const resolvedEnd = lineEnd >= resolvedStart ? lineEnd : token.map?.[1] ?? resolvedStart;
					return {
						tokenStart: i,
						tokenEnd: tokens.length,
						lineStart: resolvedStart,
						lineEnd: resolvedEnd,
						srcOffset: this.getLineStartOffset(src, resolvedStart)
					};
				}
				continue;
			}
			if (token.level === 0 && depth === 0) {
				const resolvedStart = Number.isFinite(lineStart) ? lineStart : token.map?.[0] ?? 0;
				const resolvedEnd = lineEnd >= resolvedStart ? lineEnd : token.map?.[1] ?? resolvedStart;
				return {
					tokenStart: i,
					tokenEnd: tokens.length,
					lineStart: resolvedStart,
					lineEnd: resolvedEnd,
					srcOffset: this.getLineStartOffset(src, resolvedStart)
				};
			}
		}
		return null;
	}
	getLineStartOffset(src, line) {
		if (line <= 0) return 0;
		let remaining = line;
		let pos = -1;
		while (remaining > 0) {
			pos = src.indexOf("\n", pos + 1);
			if (pos === -1) return src.length;
			remaining--;
		}
		return pos + 1;
	}
	mayContainReferenceDefinition(src) {
		if (!src.includes("]:")) return false;
		return /(?:^|\n)[ \t]{0,3}\[[^\]\n]+\]:/.test(src);
	}
	canDirectlyParseAppend(cache) {
		if (!this.endsWithBlankLine(cache.src)) return false;
		const lastSegment = this.ensureLastSegment(cache);
		if (!lastSegment) return false;
		switch (cache.tokens[lastSegment.tokenStart]?.type) {
			case "paragraph_open":
			case "heading_open":
			case "fence":
			case "code_block":
			case "html_block":
			case "hr":
			case "table_open": return true;
			default: return false;
		}
	}
	tryContainerTailAppendMerge(src, cached, env, md, lastSegment, appended) {
		if (!appended || this.mayContainReferenceDefinition(appended)) return null;
		const lastToken = cached.tokens[lastSegment.tokenStart];
		switch (lastToken?.type) {
			case "bullet_list_open":
			case "ordered_list_open": return this.tryListTailAppendMerge(src, cached, env, md, lastSegment, appended, lastToken);
			case "table_open": return this.tryTableTailAppendMerge(src, cached, env, md, lastSegment, appended, lastToken);
			default: return null;
		}
	}
	tryListTailAppendMerge(src, cached, env, md, lastSegment, appended, listOpen) {
		if (cached.src.length === 0 || cached.src.charCodeAt(cached.src.length - 1) !== 10) return null;
		const segmentLineSpan = lastSegment.lineEnd - lastSegment.lineStart;
		const segmentChars = cached.src.length - lastSegment.srcOffset;
		if (segmentLineSpan < this.MIN_LIST_LINES_FOR_MERGE && segmentChars < this.MIN_LIST_CHARS_FOR_MERGE) return null;
		const closeType = listOpen.type === "bullet_list_open" ? "bullet_list_close" : "ordered_list_close";
		let parsed;
		try {
			parsed = this.core.parse(appended, env, md).tokens;
		} catch {
			return null;
		}
		if (!this.isSingleTopLevelContainer(parsed, listOpen.type, closeType, listOpen.markup)) return null;
		const inserted = parsed.slice(1, -1);
		if (inserted.length === 0) return null;
		const lineOffset = cached.lineCount ?? countLines(cached.src);
		if (lineOffset > 0) this.shiftTokenLines(inserted, lineOffset);
		const existingMode = this.getListParagraphMode(cached.tokens, lastSegment.tokenStart, cached.tokens.length, listOpen.level);
		const appendedMode = this.getListParagraphMode(parsed, 0, parsed.length, 0);
		if (existingMode === "loose" || appendedMode === "loose" || this.endsWithBlankLine(cached.src) || (parsed[0]?.map?.[0] ?? 0) > 0) {
			this.setListParagraphVisibility(cached.tokens, lastSegment.tokenStart, cached.tokens.length, listOpen.level, false);
			this.setListParagraphVisibility(inserted, 0, inserted.length, listOpen.level, false);
		}
		cached.tokens.splice(cached.tokens.length - 1, 0, ...inserted);
		cached.src = src;
		cached.env = env;
		cached.globalStateReason = detectGlobalMarkdownState(src);
		cached.lineCount = countLines(src);
		if (listOpen.map) listOpen.map[1] = this.getDocLineCount(src);
		cached.lastSegment = {
			tokenStart: lastSegment.tokenStart,
			tokenEnd: cached.tokens.length,
			lineStart: lastSegment.lineStart,
			lineEnd: this.getDocLineCount(src),
			srcOffset: lastSegment.srcOffset
		};
		return cached.tokens;
	}
	tryTableTailAppendMerge(src, cached, env, md, lastSegment, appended, tableOpen) {
		if (cached.src.length === 0 || cached.src.charCodeAt(cached.src.length - 1) !== 10) return null;
		if (/(?:^|\n)[ \t]*\n/.test(appended)) return null;
		const segmentLineSpan = lastSegment.lineEnd - lastSegment.lineStart;
		const segmentChars = cached.src.length - lastSegment.srcOffset;
		if (segmentLineSpan < this.MIN_TABLE_LINES_FOR_MERGE && segmentChars < this.MIN_TABLE_CHARS_FOR_MERGE) return null;
		const tableContext = this.getTableHeaderContext(cached.src.slice(lastSegment.srcOffset));
		if (!tableContext) return null;
		const syntheticSrc = `${tableContext}${appended}`;
		let parsed;
		try {
			parsed = this.core.parse(syntheticSrc, env, md).tokens;
		} catch {
			return null;
		}
		if (!this.isSingleTopLevelContainer(parsed, "table_open", "table_close")) return null;
		if ((parsed[0]?.map?.[1] ?? -1) !== this.getDocLineCount(syntheticSrc)) return null;
		const parsedSection = this.getTableBodySection(parsed, 0, parsed.length, 0);
		const cachedSection = this.getTableBodySection(cached.tokens, lastSegment.tokenStart, cached.tokens.length, tableOpen.level);
		if (!parsedSection || !cachedSection || parsedSection.tbodyOpenIndex < 0 || parsedSection.tbodyCloseIndex < 0) return null;
		const inserted = cachedSection.tbodyOpenIndex >= 0 ? parsed.slice(parsedSection.tbodyOpenIndex + 1, parsedSection.tbodyCloseIndex) : parsed.slice(parsedSection.tbodyOpenIndex, parsedSection.tbodyCloseIndex + 1);
		if (inserted.length === 0) return null;
		const lineOffset = lastSegment.lineEnd - 2;
		if (lineOffset !== 0) this.shiftTokenLines(inserted, lineOffset);
		const insertAt = cachedSection.tbodyCloseIndex >= 0 ? cachedSection.tbodyCloseIndex : cachedSection.tableCloseIndex;
		cached.tokens.splice(insertAt, 0, ...inserted);
		cached.src = src;
		cached.env = env;
		cached.globalStateReason = detectGlobalMarkdownState(src);
		cached.lineCount = countLines(src);
		const nextDocLineCount = this.getDocLineCount(src);
		if (tableOpen.map) tableOpen.map[1] = nextDocLineCount;
		if (cachedSection.tbodyOpenIndex >= 0) {
			const tbodyOpen = cached.tokens[cachedSection.tbodyOpenIndex];
			if (tbodyOpen?.map) tbodyOpen.map[1] = nextDocLineCount;
		}
		cached.lastSegment = {
			tokenStart: lastSegment.tokenStart,
			tokenEnd: cached.tokens.length,
			lineStart: lastSegment.lineStart,
			lineEnd: nextDocLineCount,
			srcOffset: lastSegment.srcOffset
		};
		return cached.tokens;
	}
	getTableHeaderContext(src) {
		const firstBreak = src.indexOf("\n");
		if (firstBreak < 0) return null;
		const secondBreak = src.indexOf("\n", firstBreak + 1);
		if (secondBreak < 0) return null;
		return src.slice(0, secondBreak + 1);
	}
	getTableBodySection(tokens, start, end, tableLevel) {
		if (start < 0 || start >= end || tokens[start]?.type !== "table_open") return null;
		let tableCloseIndex = -1;
		for (let i = end - 1; i > start; i--) {
			const token = tokens[i];
			if (token.type === "table_close" && token.level === tableLevel) {
				tableCloseIndex = i;
				break;
			}
		}
		if (tableCloseIndex < 0) return null;
		let tbodyOpenIndex = -1;
		let tbodyCloseIndex = -1;
		for (let i = start + 1; i < tableCloseIndex; i++) {
			const token = tokens[i];
			if (token.type === "tbody_open" && token.level === tableLevel + 1) {
				tbodyOpenIndex = i;
				break;
			}
		}
		if (tbodyOpenIndex >= 0) {
			for (let i = tableCloseIndex - 1; i > tbodyOpenIndex; i--) {
				const token = tokens[i];
				if (token.type === "tbody_close" && token.level === tableLevel + 1) {
					tbodyCloseIndex = i;
					break;
				}
			}
			if (tbodyCloseIndex < 0) return null;
		}
		return {
			tableCloseIndex,
			tbodyOpenIndex,
			tbodyCloseIndex
		};
	}
	isSingleTopLevelContainer(tokens, openType, closeType, markup) {
		if (tokens.length < 2) return false;
		const first = tokens[0];
		const last = tokens[tokens.length - 1];
		if (first.type !== openType || last.type !== closeType || first.level !== 0 || last.level !== 0) return false;
		if (markup !== void 0 && first.markup !== markup) return false;
		let depth = 0;
		for (let i = 0; i < tokens.length; i++) {
			const token = tokens[i];
			if (token.level === 0 && i > 0 && i < tokens.length - 1 && depth === 0) return false;
			if (token.nesting > 0) depth += token.nesting;
			else if (token.nesting < 0) depth += token.nesting;
		}
		return depth === 0;
	}
	getListParagraphMode(tokens, start, end, listLevel) {
		let sawHidden = false;
		let sawVisible = false;
		const paragraphLevel = listLevel + 2;
		for (let i = start; i < end; i++) {
			const token = tokens[i];
			if (token.type !== "paragraph_open" || token.level !== paragraphLevel) continue;
			if (token.hidden) sawHidden = true;
			else sawVisible = true;
			if (sawHidden && sawVisible) return "loose";
		}
		if (sawVisible) return "loose";
		if (sawHidden) return "tight";
		return "none";
	}
	setListParagraphVisibility(tokens, start, end, listLevel, hidden) {
		const paragraphLevel = listLevel + 2;
		for (let i = start; i < end; i++) {
			const token = tokens[i];
			if ((token.type === "paragraph_open" || token.type === "paragraph_close") && token.level === paragraphLevel) token.hidden = hidden;
		}
	}
	shouldPreferTailReparseForAppend(cache) {
		const lastSegment = this.ensureLastSegment(cache);
		if (!lastSegment) return false;
		switch (cache.tokens[lastSegment.tokenStart]?.type) {
			case "bullet_list_open":
			case "ordered_list_open":
			case "blockquote_open":
			case "table_open": return true;
			case "paragraph_open":
			case "code_block":
			case "html_block": return !this.endsWithBlankLine(cache.src);
			default: return false;
		}
	}
	endsWithBlankLine(src) {
		const len = src.length;
		if (len < 2 || src.charCodeAt(len - 1) !== 10) return false;
		let pos = len - 2;
		while (pos >= 0) {
			const ch = src.charCodeAt(pos);
			if (ch === 32 || ch === 9) {
				pos--;
				continue;
			}
			return ch === 10;
		}
		return true;
	}
	getDocLineCount(src) {
		const lines = countLines(src);
		if (src.length === 0) return 0;
		return src.charCodeAt(src.length - 1) === 10 ? lines : lines + 1;
	}
	shiftTokenLines(tokens, offset) {
		if (offset === 0) return;
		const stack = [...tokens];
		while (stack.length > 0) {
			const token = stack.pop();
			if (token.map) {
				token.map[0] += offset;
				token.map[1] += offset;
			}
			if (token.children) for (let i = token.children.length - 1; i >= 0; i--) stack.push(token.children[i]);
		}
	}
};

//#endregion
//#region src/plugins/with-renderer.ts
/**
* Attach or refresh rendering methods on a markdown-it-shaped instance.
* The main markdown-it-ts entry already exposes renderer methods; this helper is
* for custom instances that provide parse/parseInline but need renderer methods.
*/
function withRenderer(md, options) {
	if (!md.renderer) md.renderer = new renderer_default();
	if (options && typeof options === "object") md.set(options);
	md.render = function(src, env = {}) {
		const tokens = this.parse(src, env);
		return this.renderer.render(tokens, this.options, env);
	};
	md.renderAsync = async function(src, env = {}) {
		const tokens = this.parse(src, env);
		return this.renderer.renderAsync(tokens, this.options, env);
	};
	md.renderInline = function(src, env = {}) {
		const tokens = this.parseInline(src, env);
		return this.renderer.render(tokens, this.options, env);
	};
	return md;
}

//#endregion
//#region src/index.ts
const config = {
	default: default_default,
	zero: zero_default,
	commonmark: commonmark_default
};
function getParserRuleVersions(md) {
	return {
		core: md.core.ruler.version,
		block: md.block.ruler.version,
		inline: md.inline.ruler.version,
		inline2: md.inline.ruler2.version
	};
}
function hasParserRuleChanges(md, initial) {
	return md.core.ruler.version !== initial.core || md.block.ruler.version !== initial.block || md.inline.ruler.version !== initial.inline || md.inline.ruler2.version !== initial.inline2;
}
function applyExperimentalOptions(options) {
	return options.experimental ? {
		...options,
		...options.experimental
	} : options;
}
function hasOwnOption(obj, key) {
	if (!obj) return false;
	if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] !== void 0) return true;
	const experimental = obj.experimental;
	return !!experimental && Object.prototype.hasOwnProperty.call(experimental, key) && experimental[key] !== void 0;
}
function hasExplicitChunkOverride(presetOptions, userOptions, keys) {
	for (let i = 0; i < keys.length; i++) {
		const key = keys[i];
		if (hasOwnOption(userOptions, key) || hasOwnOption(presetOptions, key)) return true;
	}
	return false;
}
function hasExplicitOption(presetOptions, userOptions, key) {
	return hasOwnOption(userOptions, key) || hasOwnOption(presetOptions, key);
}
function setFullChunkStrategyDiagnostics(env, reason) {
	const chunkInfo = getParseDiagnostics(env)?.chunk;
	if (chunkInfo?.fallback) {
		setStrategyDiagnostics(env, {
			area: "parse",
			path: "plain",
			reason: `global-state:${chunkInfo.fallbackReason || "unknown"}`
		});
		return;
	}
	setStrategyDiagnostics(env, {
		area: "parse",
		path: "full-chunk",
		chunked: true,
		reason
	});
}
function markdownIt(presetName, options) {
	let opts = {
		html: false,
		xhtmlOut: false,
		breaks: false,
		langPrefix: "language-",
		linkify: false,
		typographer: false,
		quotes: "“”‘’",
		highlight: null,
		maxNesting: 100,
		stream: false,
		streamOptimizationMinSize: 1e3,
		streamChunkedFallback: false,
		streamChunkSizeChars: 1e4,
		streamChunkSizeLines: 200,
		streamChunkFenceAware: true,
		streamChunkAdaptive: true,
		streamChunkTargetChunks: 8,
		streamChunkMaxChunks: void 0,
		streamSkipCacheAboveChars: 1e6,
		streamSkipCacheAboveLines: 1e5,
		fullChunkedFallback: false,
		fullChunkThresholdChars: 2e4,
		fullChunkThresholdLines: 400,
		fullChunkSizeChars: 1e4,
		fullChunkSizeLines: 200,
		fullChunkFenceAware: true,
		fullChunkAdaptive: true,
		fullChunkTargetChunks: 8,
		fullChunkMaxChunks: void 0,
		autoTuneChunks: true,
		autoUnbounded: true,
		autoUnboundedThresholdChars: 4e6,
		autoUnboundedThresholdLines: 8e4
	};
	let presetToUse = "default";
	let userOptions;
	if (!options && typeof presetName !== "string") {
		userOptions = presetName;
		presetToUse = "default";
	} else if (typeof presetName === "string") {
		presetToUse = presetName;
		userOptions = options;
	}
	const preset = config[presetToUse];
	if (!preset) throw new Error(`Wrong \`markdown-it\` preset "${presetToUse}", check name`);
	if (preset?.options) opts = {
		...opts,
		...preset.options
	};
	if (userOptions) opts = {
		...opts,
		...userOptions
	};
	opts = applyExperimentalOptions(opts);
	if (typeof opts.quotes === "string") {
		const quotesStr = opts.quotes;
		if (quotesStr.length >= 4) opts.quotes = [
			quotesStr[0],
			quotesStr[1],
			quotesStr[2],
			quotesStr[3]
		];
		else opts.quotes = [
			"“",
			"”",
			"‘",
			"’"
		];
	}
	let explicitFullChunkConfig = hasExplicitChunkOverride(preset?.options, userOptions, [
		"fullChunkSizeChars",
		"fullChunkSizeLines",
		"fullChunkMaxChunks"
	]);
	let explicitStreamChunkConfig = hasExplicitChunkOverride(preset?.options, userOptions, [
		"streamChunkSizeChars",
		"streamChunkSizeLines",
		"streamChunkMaxChunks"
	]);
	let explicitFullChunkFallbackSetting = hasExplicitOption(preset?.options, userOptions, "fullChunkedFallback");
	let explicitStreamChunkFallbackSetting = hasExplicitOption(preset?.options, userOptions, "streamChunkedFallback");
	let usedPlugin = false;
	let initialParserRuleVersions = null;
	const core = new ParserCore();
	let renderer = null;
	const getRenderer = () => {
		if (!renderer) renderer = new renderer_default(opts);
		return renderer;
	};
	let streamParser = null;
	const getStreamParser = () => {
		if (!streamParser) streamParser = new StreamParser(core);
		return streamParser;
	};
	let linkifyInstance = null;
	const getLinkify = () => {
		if (!linkifyInstance) linkifyInstance = new LinkifyIt();
		return linkifyInstance;
	};
	const canUseImplicitLargeInputStrategy = (instance) => {
		return !usedPlugin && !!initialParserRuleVersions && !hasParserRuleChanges(instance, initialParserRuleVersions);
	};
	const md = {
		core,
		block: core.block,
		inline: core.inline,
		get linkify() {
			const inst = getLinkify();
			Object.defineProperty(this, "linkify", {
				value: inst,
				writable: true,
				configurable: true
			});
			return inst;
		},
		get renderer() {
			const r = getRenderer();
			Object.defineProperty(this, "renderer", {
				value: r,
				writable: true,
				configurable: true
			});
			return r;
		},
		options: opts,
		__explicitFullChunkConfig: explicitFullChunkConfig,
		__explicitStreamChunkConfig: explicitStreamChunkConfig,
		__explicitFullChunkFallbackSetting: explicitFullChunkFallbackSetting,
		__explicitStreamChunkFallbackSetting: explicitStreamChunkFallbackSetting,
		__canUseImplicitLargeInputStrategy() {
			return canUseImplicitLargeInputStrategy(this);
		},
		set(newOpts) {
			const resolvedNewOpts = applyExperimentalOptions(newOpts);
			this.options = {
				...this.options,
				...resolvedNewOpts
			};
			if (hasOwnOption(newOpts, "fullChunkSizeChars") || hasOwnOption(newOpts, "fullChunkSizeLines") || hasOwnOption(newOpts, "fullChunkMaxChunks")) {
				explicitFullChunkConfig = true;
				this.__explicitFullChunkConfig = true;
			}
			if (hasOwnOption(newOpts, "streamChunkSizeChars") || hasOwnOption(newOpts, "streamChunkSizeLines") || hasOwnOption(newOpts, "streamChunkMaxChunks")) {
				explicitStreamChunkConfig = true;
				this.__explicitStreamChunkConfig = true;
			}
			if (hasOwnOption(newOpts, "fullChunkedFallback")) {
				explicitFullChunkFallbackSetting = true;
				this.__explicitFullChunkFallbackSetting = true;
			}
			if (hasOwnOption(newOpts, "streamChunkedFallback")) {
				explicitStreamChunkFallbackSetting = true;
				this.__explicitStreamChunkFallbackSetting = true;
			}
			if (renderer) renderer.set(resolvedNewOpts);
			if (typeof resolvedNewOpts.stream === "boolean") {
				this.stream.enabled = resolvedNewOpts.stream;
				if (streamParser) {
					streamParser.reset();
					streamParser.resetStats();
				}
			}
			return this;
		},
		configure(presets) {
			const p = typeof presets === "string" ? config[presets] : presets;
			if (!p) throw new Error("Wrong `markdown-it` preset, can't be empty");
			if (p.options) this.set(p.options);
			if (p.components) {
				const c = p.components;
				if (c.core?.rules) this.core.ruler.enableOnly(c.core.rules);
				if (c.block?.rules) this.block.ruler.enableOnly(c.block.rules);
				if (c.inline?.rules) this.inline.ruler.enableOnly(c.inline.rules);
				if (c.inline2?.rules) this.inline.ruler2.enableOnly(c.inline2.rules);
			}
			return this;
		},
		enable(list, ignoreInvalid) {
			const names = Array.isArray(list) ? list : [list];
			const managers = [
				this.core?.ruler,
				this.block?.ruler,
				this.inline?.ruler,
				this.inline?.ruler2
			];
			const found = /* @__PURE__ */ new Set();
			for (const m of managers) {
				if (!m) continue;
				const enabled = m.enable(names, true);
				for (let i = 0; i < enabled.length; i++) found.add(enabled[i]);
			}
			if (!ignoreInvalid) {
				const missed = names.filter((name) => !found.has(name));
				if (missed.length) throw new Error(`Rules manager: invalid rule name ${missed.join(", ")}`);
			}
			return this;
		},
		disable(list, ignoreInvalid) {
			const names = Array.isArray(list) ? list : [list];
			const managers = [
				this.core?.ruler,
				this.block?.ruler,
				this.inline?.ruler,
				this.inline?.ruler2
			];
			const found = /* @__PURE__ */ new Set();
			for (const m of managers) {
				if (!m) continue;
				const disabled = m.disable(names, true);
				for (let i = 0; i < disabled.length; i++) found.add(disabled[i]);
			}
			if (!ignoreInvalid) {
				const missed = names.filter((name) => !found.has(name));
				if (missed.length) throw new Error(`Rules manager: invalid rule name ${missed.join(", ")}`);
			}
			return this;
		},
		use(plugin, ...params) {
			const fn = typeof plugin === "function" ? plugin : plugin && typeof plugin.default === "function" ? plugin.default : void 0;
			if (!fn) throw new TypeError("MarkdownIt.use: plugin must be a function");
			const args = [this, ...params];
			const thisArg = typeof plugin === "function" ? plugin : plugin;
			usedPlugin = true;
			fn.apply(thisArg, args);
			return this;
		},
		render(src, env = {}) {
			const tokens = this.parse(src, env);
			return getRenderer().render(tokens, this.options, env);
		},
		async renderAsync(src, env = {}) {
			const tokens = this.parse(src, env);
			return getRenderer().renderAsync(tokens, this.options, env);
		},
		renderIterable(chunks, env = {}) {
			const tokens = this.parseIterable(chunks, env);
			return getRenderer().render(tokens, this.options, env);
		},
		async renderAsyncIterable(chunks, env = {}) {
			const tokens = await this.parseAsyncIterable(chunks, env);
			return getRenderer().renderAsync(tokens, this.options, env);
		},
		renderInline(src, env = {}) {
			const tokens = this.parseInline(src, env);
			return getRenderer().render(tokens, this.options, env);
		},
		validateLink,
		normalizeLink,
		normalizeLinkText,
		utils: utils_exports,
		helpers: { ...helpers_exports },
		parse(src, env = {}) {
			if (typeof src !== "string") throw new TypeError("Input data should be a String");
			beginParseDiagnostics(env);
			let countedLines;
			if (!this.stream.enabled && !this.options.fullChunkedFallback) {
				if (canUseImplicitLargeInputStrategy(this)) {
					const autoUnboundedDecision = getAutoUnboundedDecision(this, src.length);
					if (autoUnboundedDecision === "yes") {
						const tokens = parseStringUnbounded(this, src, env);
						setStrategyDiagnostics(env, {
							area: "parse",
							path: "auto-unbounded",
							unbounded: true,
							reason: "char-threshold"
						});
						return tokens;
					}
					if (autoUnboundedDecision === "need-lines") countedLines = countLines(src);
				}
			}
			if (!this.stream.enabled) {
				const chars = src.length;
				const lines = countedLines ?? countLines(src);
				const auto = this.options.autoTuneChunks !== false;
				const userForcedChunk = explicitFullChunkConfig;
				const allowImplicitChunk = !explicitFullChunkFallbackSetting && canUseImplicitLargeInputStrategy(this);
				const wantsChunking = !!this.options.fullChunkedFallback;
				const shouldAutoChunk = allowImplicitChunk && chars >= 2e5;
				const autoRecommendation = auto && !userForcedChunk ? recommendFullChunkStrategy(chars, lines, this.options) : null;
				if (wantsChunking || shouldAutoChunk) {
					if (wantsChunking ? chars >= (this.options.fullChunkThresholdChars ?? 2e4) || lines >= (this.options.fullChunkThresholdLines ?? 400) : shouldAutoChunk) {
						if (autoRecommendation && autoRecommendation.strategy !== "plain") {
							const tokens = chunkedParse(this, src, env, {
								maxChunkChars: autoRecommendation.maxChunkChars,
								maxChunkLines: autoRecommendation.maxChunkLines,
								fenceAware: autoRecommendation.fenceAware,
								maxChunks: autoRecommendation.maxChunks
							});
							setFullChunkStrategyDiagnostics(env, wantsChunking ? "explicit-full-chunk" : "default-large-string");
							return tokens;
						}
						if (wantsChunking) {
							const clamp = (v, lo, hi) => v < lo ? lo : v > hi ? hi : v;
							const adaptive = this.options.fullChunkAdaptive !== false;
							const target = this.options.fullChunkTargetChunks ?? 8;
							const dynMaxChunkChars = clamp(Math.ceil(chars / target), 8e3, 64e3);
							const dynMaxChunkLines = clamp(Math.ceil(lines / target), 150, 700);
							const maxChunkChars = adaptive ? dynMaxChunkChars : this.options.fullChunkSizeChars ?? 1e4;
							const maxChunkLines = adaptive ? dynMaxChunkLines : this.options.fullChunkSizeLines ?? 200;
							const maxChunks = adaptive ? clamp(Math.ceil(chars / 64e3), target, 32) : this.options.fullChunkMaxChunks;
							const tokens = chunkedParse(this, src, env, {
								maxChunkChars,
								maxChunkLines,
								fenceAware: this.options.fullChunkFenceAware ?? true,
								maxChunks
							});
							setFullChunkStrategyDiagnostics(env, "explicit-full-chunk");
							return tokens;
						}
					}
				}
				if (countedLines !== void 0 && canUseImplicitLargeInputStrategy(this) && shouldAutoUseUnbounded(this, chars, lines)) {
					const tokens = parseStringUnbounded(this, src, env);
					setStrategyDiagnostics(env, {
						area: "parse",
						path: "auto-unbounded",
						unbounded: true,
						reason: "line-threshold"
					});
					return tokens;
				}
			}
			const currentGlobalStateReason = detectGlobalMarkdownState(src);
			setStrategyDiagnostics(env, {
				area: "parse",
				path: "plain",
				reason: "default-plain"
			});
			return runWithKnownGlobalMarkdownState(env, currentGlobalStateReason, () => {
				return core.parse(src, env, this).tokens;
			});
		},
		parseIterable(chunks, env = {}) {
			beginParseDiagnostics(env);
			return parseIterable(this, chunks, env);
		},
		parseAsyncIterable(chunks, env = {}) {
			beginParseDiagnostics(env);
			return parseAsyncIterable(this, chunks, env);
		},
		parseIterableToSink(chunks, onChunkTokens, env = {}) {
			beginParseDiagnostics(env);
			return parseIterableToSink(this, chunks, onChunkTokens, env);
		},
		parseAsyncIterableToSink(chunks, onChunkTokens, env = {}) {
			beginParseDiagnostics(env);
			return parseAsyncIterableToSink(this, chunks, onChunkTokens, env);
		},
		parseInline(src, env = {}) {
			if (typeof src !== "string") throw new TypeError("Input data should be a String");
			beginParseDiagnostics(env);
			if (getKnownGlobalMarkdownState(env)) resetKnownGlobalMarkdownState(env);
			const state = core.createState(src, env, this);
			state.inlineMode = true;
			core.process(state);
			return state.tokens;
		}
	};
	md.stream = {
		enabled: Boolean(opts.stream),
		parse(src, env) {
			if (!md.stream.enabled) return md.parse(src, env ?? {});
			return getStreamParser().parse(src, env, md);
		},
		reset() {
			getStreamParser().reset();
		},
		peek() {
			return streamParser ? streamParser.peek() : [];
		},
		stats() {
			return streamParser ? streamParser.getStats() : {
				total: 0,
				cacheHits: 0,
				appendHits: 0,
				unboundedAppendHits: 0,
				tailHits: 0,
				fullParses: 0,
				resets: 0,
				chunkedParses: 0,
				lastMode: "idle"
			};
		},
		resetStats() {
			if (streamParser) streamParser.resetStats();
		}
	};
	if (preset?.components) {
		const c = preset.components;
		if (c.core?.rules) md.core.ruler.enableOnly(c.core.rules);
		if (c.block?.rules) md.block.ruler.enableOnly(c.block.rules);
		if (c.inline?.rules) md.inline.ruler.enableOnly(c.inline.rules);
		if (c.inline2?.rules) md.inline.ruler2.enableOnly(c.inline2.rules);
	}
	initialParserRuleVersions = getParserRuleVersions(md);
	return md;
}
const MarkdownItExport = markdownIt;
var src_default = MarkdownItExport;

//#endregion
export { Token, src_default as default, parse, parseInline, withRenderer };